import React, { useState, useEffect } from "react";
import { Card, ListGroup, Dropdown, Spinner } from "react-bootstrap";
import styles from "./Card.module.css";
import GenericFHIRViewerModal from "../modals/ResourceViewerModal";
import { formatLocalDate } from "../utils/dateUtils";
import ConditionIcon from "../assets/condition-1.png"; // replace with your icon
import { resolveName } from "../utils/messageUtils";

function ConditionCard({
    title,
    inContext,
    conditionData,
    fhirClient,
    patientId,
    enableSearch = false,
    onConditionChange,
    isReply,
    depth = 6,
}) {
    const [error, setError] = useState("");
    const [showModal, setShowModal] = useState(false);
    const [modalConditionDetails, setModalConditionDetails] = useState(null);
    const [conditionHistory, setConditionHistory] = useState([]);
    const [selectedCondition, setSelectedCondition] = useState(conditionData || null);
    const [historyLoading, setHistoryLoading] = useState(false);

    const handleModalClose = () => setShowModal(false);

    if (!title) title = resolveName(conditionData);  

    /** Sync incoming condition */
    useEffect(() => {
        if (conditionData?.error) {
            setError(`Error loading condition (code: ${conditionData.error}).`);
            setSelectedCondition(null);
        } else {
            setError("");
            setSelectedCondition(conditionData || null);
        }
    }, [conditionData]);

    /** Fetch patient condition history */
    useEffect(() => {
        if (!enableSearch || !patientId || !fhirClient) return;

        const fetchConditions = async () => {
            setHistoryLoading(true);
            try {
                const response = await fhirClient.request(
                    `Condition?patient=Patient/${patientId}&_sort=-recorded-date&_count=10`
                );
                const found = response.entry?.map((e) => e.resource) || [];
                setConditionHistory(found);
            } catch (e) {
                console.warn("Condition history fetch error:", e);
                setConditionHistory([]);
            } finally {
                setHistoryLoading(false);
            }
        };

        fetchConditions();
    }, [enableSearch, patientId, fhirClient]);

    /** Auto-select newest condition */
    useEffect(() => {
        if (!isReply && enableSearch) {
            if (conditionHistory.length > 0) {
                const first = conditionHistory[0];
                setSelectedCondition(first);
                if (onConditionChange) onConditionChange(first);
            } else {
                setSelectedCondition(null);
                if (onConditionChange) onConditionChange(null);
            }
        }
    }, [conditionHistory]);

    /** Open modal with fully loaded resource */
    const handleModalOpen = async () => {
        if (!selectedCondition?.id) return;

        try {
            const details = await fhirClient.request(`Condition/${selectedCondition.id}`);
            setModalConditionDetails(details);
        } catch {
            setModalConditionDetails(null);
        }
        setShowModal(true);
    };

    const handleSelectCondition = (c) => {
        setSelectedCondition(c);
        if (onConditionChange) onConditionChange(c);
    };

    /** Field rendering */
    const fieldRenderers = [
        <ListGroup.Item className={styles.listItem} key="code">
            <strong>Condition:</strong>{" "}
            {selectedCondition?.code?.text ||
                selectedCondition?.code?.coding?.[0]?.display ||
                "N/A"}
        </ListGroup.Item>,

        <ListGroup.Item className={styles.listItem} key="clinicalStatus">
            <strong>Status:</strong>{" "}
            {selectedCondition?.clinicalStatus?.coding?.[0]?.display ||
                selectedCondition?.clinicalStatus?.coding?.[0]?.code ||
                "N/A"}
        </ListGroup.Item>,

        <ListGroup.Item className={styles.listItem} key="verificationStatus">
            <strong>Verification:</strong>{" "}
            {selectedCondition?.verificationStatus?.coding?.[0]?.display ||
                selectedCondition?.verificationStatus?.coding?.[0]?.code ||
                "N/A"}
        </ListGroup.Item>,

        <ListGroup.Item className={styles.listItem} key="recordedDate">
            <strong>Recorded:</strong>{" "}
            {formatLocalDate(selectedCondition?.recordedDate)}
        </ListGroup.Item>,

        <ListGroup.Item className={styles.listItem} key="onset">
            <strong>Onset:</strong>{" "}
            {formatLocalDate(
                selectedCondition?.onsetDateTime ||
                selectedCondition?.onsetPeriod?.start
            )}
        </ListGroup.Item>,

        <ListGroup.Item className={styles.listItem} key="asserter">
            <strong>Asserter:</strong>{" "}
            {selectedCondition?.asserter?.display ||
                selectedCondition?.asserter?.reference ||
                "N/A"}
        </ListGroup.Item>,
    ];

    return (
        <>
            <Card className={styles.shrinkCard}>
                {/* HEADER */}
                <Card.Header
                    className={`d-flex justify-content-between align-items-center ${
                        inContext ? styles.inContextCardHeader : styles.cardHeader
                    }`}
                    onClick={handleModalOpen}
                    style={{ cursor: "pointer" }}
                >
                    {/* LEFT SIDE */}
                    <div className="d-flex align-items-center" style={{ pointerEvents: "none" }}>
                        <img
                            src={ConditionIcon}
                            alt="Condition Icon"
                            className={styles.fhirIcon}
                        />

                        <h6 className="mb-0" style={{ marginLeft: "0.5rem" }}>
                            {title?.length > 75
                                ? `Condition: ${title.substring(0, 75)}…`
                                : `Condition: ${title}`}
                        </h6>
                    </div>

                    {/* RIGHT SIDE */}
                    <div onClick={(e) => e.stopPropagation()}>
                        {depth > 0 && enableSearch && !isReply && (
                            historyLoading ? (
                                <Spinner animation="border" size="sm" className="me-2" />
                            ) : (
                                <Dropdown
                                    onSelect={(eventKey) =>
                                        handleSelectCondition(
                                            conditionHistory.find((c) => c.id === eventKey)
                                        )
                                    }
                                >
                                    <Dropdown.Toggle variant="outline-primary" size="sm">
                                        Select Condition
                                    </Dropdown.Toggle>

                                    <Dropdown.Menu
                                        style={{ maxHeight: "300px", overflowY: "auto" }}
                                    >
                                        {conditionHistory.map((c) => (
                                            <Dropdown.Item key={c.id} eventKey={c.id}>
                                                {c.code?.text ||
                                                    c.code?.coding?.[0]?.display ||
                                                    "Condition"}{" "}
                                                · {formatLocalDate(c.recordedDate)}
                                            </Dropdown.Item>
                                        ))}
                                    </Dropdown.Menu>
                                </Dropdown>
                            )
                        )}
                    </div>
                </Card.Header>

                {/* BODY */}
                {depth > 0 && (
                    <Card.Body>
                        {error ? (
                            <p className={styles.errorText}>{error}</p>
                        ) : (
                            <ListGroup variant="flush">
                                {fieldRenderers.slice(0, depth)}
                            </ListGroup>
                        )}
                    </Card.Body>
                )}
            </Card>

            {/* MODAL VIEWER */}
            <GenericFHIRViewerModal
                show={showModal}
                onClose={handleModalClose}
                resource={modalConditionDetails}
                fhirClient={fhirClient}
            />
        </>
    );
}

export default ConditionCard;
